import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Siren, MapPin, UserPlus, Phone, ShieldCheck, Clock } from "lucide-react";
import { toast } from "sonner";

export default function Safety() {
  const [bookings, setBookings] = useState([]);
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [sharing, setSharing] = useState(false);

  useEffect(() => { api.get("/bookings").then(({ data }) => setBookings(data.bookings.filter(b => b.status === "confirmed"))); }, []);

  const sos = async () => {
    try {
      await api.post("/safety/event", { type: "sos", data: { ts: Date.now() } });
      toast.success("SOS sent. Our safety team and your trusted contact are alerted.");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const saveContact = async () => {
    if (!contactName || !contactPhone) return toast.error("Enter name and phone");
    await api.post("/safety/trusted-contact", null, { params: { name: contactName, phone: contactPhone } });
    toast.success("Trusted contact saved");
  };
  const shareLocation = async () => {
    setSharing(true);
    if (!navigator.geolocation) { await post(null); return; }
    navigator.geolocation.getCurrentPosition(
      async (pos) => post({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      async () => post(null)
    );
    async function post(coords) {
      await api.post("/safety/event", { type: "location_share", data: coords || { note: "location unavailable" } });
      toast.success("Live location shared with your trusted contact");
      setSharing(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-sand">
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-10">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-sage">Safety Suite</span>
        <h1 className="font-display text-4xl md:text-5xl font-black text-charcoal mt-2">Your safety toolkit</h1>
        <p className="text-charcoal/60 mt-2">Public venues only. No sexual services. These tools are always one tap away.</p>

        {/* SOS */}
        <div className="bg-signal rounded-3xl p-8 mt-8 text-white text-center">
          <Siren className="w-10 h-10 mx-auto mb-3" />
          <h2 className="font-display text-2xl font-bold">Emergency SOS</h2>
          <p className="text-white/80 text-sm mt-1 mb-5">Instantly alert our 24/7 safety team and your trusted contact.</p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button data-testid="sos-button" className="rounded-full bg-white text-signal hover:bg-white/90 h-12 px-10 text-base font-bold">Trigger SOS</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader><AlertDialogTitle>Send an SOS alert?</AlertDialogTitle>
                <AlertDialogDescription>This immediately notifies our safety team (human-in-the-loop) and your trusted contact. Use only if you feel unsafe.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="sos-confirm" onClick={sos} className="rounded-full bg-signal hover:bg-signal/90">Send SOS</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        <div className="grid md:grid-cols-2 gap-5 mt-6">
          {/* Trusted contact */}
          <div className="bg-white rounded-2xl border border-border p-6">
            <UserPlus className="w-6 h-6 text-terracotta mb-2" strokeWidth={1.5} />
            <h3 className="font-display text-xl font-bold text-charcoal">Trusted contact</h3>
            <p className="text-sm text-charcoal/60 mb-4">They'll be alerted on SOS and can see your live location.</p>
            <Label className="text-sm font-semibold">Name</Label>
            <Input data-testid="contact-name" value={contactName} onChange={(e) => setContactName(e.target.value)} className="mt-1 rounded-xl mb-3" placeholder="e.g. Priya (sister)" />
            <Label className="text-sm font-semibold">Phone</Label>
            <Input data-testid="contact-phone" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} className="mt-1 rounded-xl mb-4" placeholder="+91..." />
            <Button data-testid="save-contact" onClick={saveContact} className="w-full rounded-full bg-charcoal hover:bg-charcoal/90 text-white h-10">Save contact</Button>
          </div>

          {/* Live location */}
          <div className="bg-white rounded-2xl border border-border p-6">
            <MapPin className="w-6 h-6 text-sage mb-2" strokeWidth={1.5} />
            <h3 className="font-display text-xl font-bold text-charcoal">Live location share</h3>
            <p className="text-sm text-charcoal/60 mb-4">Share where you are during a meet with your trusted contact.</p>
            <Button data-testid="share-location" onClick={shareLocation} disabled={sharing} className="w-full rounded-full bg-sage hover:bg-sage/90 text-white h-10">{sharing ? "Sharing..." : "Share my live location"}</Button>
            <div className="mt-4 space-y-1 text-sm text-charcoal/60">
              <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-sage" /> Masked phone numbers</p>
              <p className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-sage" /> Verified partner venues</p>
            </div>
          </div>
        </div>

        {/* Active check-ins */}
        <div className="bg-white rounded-2xl border border-border p-6 mt-6">
          <h3 className="font-display text-xl font-bold text-charcoal flex items-center gap-2"><Clock className="w-5 h-5 text-terracotta" /> Upcoming — check in/out</h3>
          {bookings.length === 0 ? <p className="text-sm text-charcoal/50 mt-3">No upcoming hangouts.</p> :
            <div className="mt-3 space-y-2">
              {bookings.map((b) => (
                <div key={b.id} className="flex items-center justify-between bg-sand rounded-xl p-3">
                  <div><p className="font-semibold text-sm text-charcoal">{b.host_name} · {b.activity}</p><p className="text-xs text-charcoal/60">{b.venue}</p></div>
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${b.checkin_at ? "bg-sage/15 text-sage" : "bg-sun/20 text-charcoal"}`}>{b.checkin_at ? "Checked in" : "Not started"}</span>
                </div>
              ))}
            </div>}
        </div>
      </div>
    </div>
  );
}
